import DOMPurify from 'dompurify';

const IDENTIFIER = /^[A-Za-z_][A-Za-z0-9_]*$/;

// Local examples only: never replace these with patient/practice API data.
export const TEMPLATE_PREVIEW_SAMPLES = Object.freeze({
    lead_first_name: 'Sarah',
    lead_last_name: 'Jones',
    lead_full_name: 'Sarah Jones',
    lead_email: 'sarah.jones@example.invalid',
    lead_phone: '07700 900123',
    practice_name: 'Example Dental Practice',
    practice_email: 'hello@example.invalid',
    practice_phone: '01632 960123',
    practice_full_address: '10 Example Street, Sample Town',
    treatment_name: 'Example consultation',
    booking_url: 'https://booking.example.invalid/appointment',
    appointment_date: '21 September 2026',
    appointment_time: '10:30',
    form_link: 'https://forms.example.invalid/sample',
    form_url: 'https://forms.example.invalid/sample',
    consent_form_dynamic_url: 'https://forms.example.invalid/consent',
});

const scanTags = (value, includeMustache) => {
    const text = String(value || '');
    const tokens = [];
    for (let index = 0; index < text.length;) {
        // Skip ordinary HTML and large inline image payloads in one native search.
        let nextIndex = text.indexOf('$', index);
        if (includeMustache) {
            const nextMustache = text.indexOf('{{', index);
            if (nextMustache >= 0 && (nextIndex < 0 || nextMustache < nextIndex)) nextIndex = nextMustache;
        }
        if (nextIndex < 0) break;
        index = nextIndex;
        const rest = text.slice(index);
        let token;
        if (rest.startsWith('$$')) {
            token = { raw: '$$', escaped: true };
        } else if (rest.startsWith('${')) {
            const closing = rest.indexOf('}');
            const nextDollar = rest.indexOf('$', 2);
            // An invalid opening delimiter must not consume a later valid tag.
            let raw = closing >= 0 ? rest.slice(0, closing + 1) : (rest.match(/^\$\{[^\s<$]*/) || ['${'])[0];
            if (nextDollar >= 0 && closing >= 0 && nextDollar < closing) raw = rest.slice(0, nextDollar);
            const key = raw.slice(2, -1);
            token = { raw, ...(raw.endsWith('}') && IDENTIFIER.test(key) ? { key } : { malformed: true }) };
        } else {
            const dollar = rest.match(/^\$([A-Za-z_][A-Za-z0-9_]*)/);
            const mustache = includeMustache && rest.match(/^\{\{([A-Za-z_][A-Za-z0-9_]*)\}\}/);
            const match = dollar || mustache;
            if (match) token = { raw: match[0], key: match[1] };
        }
        if (token) {
            tokens.push({ ...token, index });
            index += token.raw.length;
        } else index += 1;
    }
    return tokens;
};

// Validation is name-only. A recognized key does not prove a recipient has a value.
export const getTemplateMergeTagIssues = ({
    subject = '', body = '', knownTags = [],
    knownTagsLoaded = Array.isArray(knownTags) && knownTags.length > 0,
    includeMustache = false,
} = {}) => {
    const known = new Set(Array.isArray(knownTags) ? knownTags : []);
    const issues = [];
    let hasTags = false;
    Object.entries({ subject, body }).forEach(([field, text]) => {
        scanTags(text, includeMustache).forEach(({ raw, key, malformed, escaped }) => {
            if (escaped) return;
            hasTags = true;
            if (malformed) issues.push({ type: 'malformed', field, token: raw });
            else if (knownTagsLoaded && !known.has(key)) issues.push({ type: 'unknown', field, token: raw, key });
        });
    });
    return {
        issues,
        unknownTags: [...new Set(issues.filter((issue) => issue.type === 'unknown').map((issue) => issue.key))],
        malformedTags: [...new Set(issues.filter((issue) => issue.type === 'malformed').map((issue) => issue.token))],
        hasIssues: issues.length > 0,
        hasTags,
    };
};

// Numeric entities also protect unquoted attribute boundaries in HTML templates.
const escapeSampleHtml = (value) => String(value).replace(/[&<>"'`=\s]/g, (character) => `&#${character.charCodeAt(0)};`);

export const resolveTemplateSamplePreview = ({
    subject = '', body = '', includeMustache = false, sampleValues = TEMPLATE_PREVIEW_SAMPLES,
} = {}) => {
    const unavailable = new Set();
    const resolve = (value, html) => {
        const text = String(value || '');
        let result = '';
        let previous = 0;
        scanTags(text, includeMustache).forEach(({ index, raw, key, escaped }) => {
            result += text.slice(previous, index);
            if (escaped) result += '$';
            else if (key && Object.prototype.hasOwnProperty.call(sampleValues, key)) {
                result += html ? escapeSampleHtml(sampleValues[key]) : String(sampleValues[key]);
            } else {
                result += raw;
                if (key) unavailable.add(key);
            }
            previous = index + raw.length;
        });
        return result + text.slice(previous);
    };
    const resolvedSubject = resolve(subject, false);
    const resolvedBody = resolve(body, true);
    return { subject: resolvedSubject, body: resolvedBody, unavailableSampleKeys: [...unavailable] };
};

export const createTemplatePreviewDocument = (html = '') => {
    const clean = DOMPurify.sanitize(String(html || ''), {
        WHOLE_DOCUMENT: true,
        FORBID_TAGS: ['script', 'iframe', 'frame', 'frameset', 'object', 'embed', 'base', 'meta', 'link', 'form', 'input', 'button', 'textarea', 'select'],
        FORBID_ATTR: ['href', 'xlink:href', 'action', 'formaction', 'srcdoc', 'target'],
    });
    // First in head: template styles, pixels, fonts and embeds cannot issue requests.
    const document = new DOMParser().parseFromString(clean, 'text/html');
    const policy = document.createElement('meta');
    policy.setAttribute('http-equiv', 'Content-Security-Policy');
    policy.setAttribute('content', "default-src 'none'; style-src 'unsafe-inline'; img-src data:; base-uri 'none'; form-action 'none'");
    document.head.prepend(policy);
    return `<!doctype html>${document.documentElement.outerHTML}`;
};
